// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import withAdminAuth from "../components/HOC/withAdminAuthPage";
import { PageHeader, PageContainer } from "../components/AssetBreadCrumb";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import { FiPlay, FiEdit2, FiCode, FiDownload, FiTrash2 } from "react-icons/fi";
import { Skeleton, SkeletonGroup } from "../components/Skeleton";
import ModelsToolbar from "../components/Models/ModelsToolbar";
import { useDispatch, useSelector } from "react-redux";
import { fetchPCurves, fetchSolarEnergyModels } from "../store/pCurveSlice";
import { getMyOrganizations } from "../store/userSlice";
import { fetchProcessedModelSchemas } from "../store/processedModelSchemaSlice";
import { fetchModelSchemaTemplates } from "../store/modelSchemaTemplateSlice";
import { PCurveTable } from "../components/PerformanceCurve/PCurveTable";
import { modalViewsConst, setModalState } from "../store/modalSlice";
import { getActiveModelSchemas } from "../types/modelSchemas";
import TestProcessedModelSchema from "../components/TestProcessedModelSchema";
import { buildPostSampleForModel } from "../services/postSampleService";
import {
  DEFAULT_MODEL_SORT,
  downloadJson,
  exportModelSchema,
  filterAndSortModels,
  getAvailableCategories,
  MODEL_SORT_OPTIONS,
  parseSchemaImportFile,
} from "../utils/modelsList";

const HEAD_CLASS =
  "whitespace-nowrap border-b border-eai-border bg-eai-surface-hover px-4 py-2.5 text-left font-outfit text-eai-meta font-medium uppercase text-eai-ink-subtle";

const CELL_CLASS = "hidden px-4 py-3 font-inter text-eai-body-sm text-eai-ink-subtle md:table-cell";

const ModelsPage = () => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState(DEFAULT_MODEL_SORT);
  // Tracks which schema id was just copied, to show transient "Copied!" feedback
  const [copiedId, setCopiedId] = useState(null);
  const pCurve = useSelector((state) => state.pCurve);
  const { organizations } = useSelector((state) => state.user);
  const processedModelSchemas = useSelector((state) => state.processedModelSchemas);
  // Templates carry the requiresTraining / trainingCost metadata; schemas link to them via schemaType.
  const templatesById = useSelector((state) => state.modelSchemaTemplates?.templates || {});

  useEffect(() => {
    // Load processed model schemas from DynamoDB
    dispatch(fetchProcessedModelSchemas());

    // Load model templates so we can show training metadata per schema (keyed by schemaType)
    dispatch(fetchModelSchemaTemplates());

    // Load existing performance curves
    dispatch(fetchPCurves());
    dispatch(fetchSolarEnergyModels());

    // Load organizations data if not already loaded
    if (!organizations.loaded) {
      dispatch(getMyOrganizations());
    }
  }, []);

  // Hidden file input used by the "Import" button to upload an exported schema JSON
  const importInputRef = useRef(null);

  // Surface an error/info message through the app's notification modal
  const notify = (header, details) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.notification,
        properties: { __modalSize: "sm", header, details },
      })
    );
  };

  const handleAddModel = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.modelGarden,
        properties: {}
      })
    );
  };

  const handleImportModel = () => {
    importInputRef.current?.click();
  };

  // Read an exported ProcessedModelSchema JSON and open the schema wizard pre-filled to create a NEW schema from it (a fresh id is generated on submit, so no collision).
  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-fires onChange
    e.target.value = "";
    if (!file) return;

    let parsed;
    try {
      parsed = await parseSchemaImportFile(file);
    } catch (err) {
      notify("Import failed", err?.message || "Could not read schema file.");
      return;
    }

    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.newProcessedModelSchema,
        properties: { __modalSize: "md", importedSchema: parsed }
      })
    );
  };

  const handleAddEnergyModel = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.energyModelSelect,
        properties: null,
      })
    );
  };

  const handleEditModel = (model) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.newProcessedModelSchema,
        properties: { __modalSize: "md", schemaToEdit: model }
      })
    );
  };

  const handleDeleteModel = (model) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.generalDelete,
        properties: {
          __modalSize: "sm",
          title: "Delete Model",
          itemName: model.name,
          itemId: model.id,
          itemType: "processedModelSchema",
        },
      })
    );
  };

  const handleExportModel = async (model) => {
    try {
      await exportModelSchema(model);
    } catch (error) {
      console.error("Error exporting model schema:", error);
      notify("Export failed", error?.message || "Failed to export model schema. Please try again.");
    }
  };

  // Download the body this model's run POSTs to the template's executionUrl, inputs included (the same builder as the
  // Admin-only button on Data & Models), so endpoint developers can see the exact payload shape.
  const handlePostSampleModel = async (model) => {
    try {
      const { fileName, body } = await buildPostSampleForModel(model.id);
      downloadJson(JSON.stringify(body, null, 2), fileName);
    } catch (error) {
      console.error("Error building post sample:", error);
      notify("Post sample failed", error?.message || "Failed to build post sample. Please try again.");
    }
  };

  // Copy a schema id to the clipboard (for pasting into run-schema.json test events)
  const handleCopyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500);
    } catch (err) {
      console.error("Failed to copy schema id:", err);
    }
  };

  const handleRunModel = (model) => {
    console.log("Run model:", model);
    // In the future, this will trigger the model execution via the executionUrl
    alert(`Model "${model.name}" would be executed at:\n${model.executionUrl}`);
  };

  // Get models from Redux store instead of local state. Memoised so the derived lists below aren't rebuilt on every keystroke or transient "Copied!" state change.
  const models = useMemo(
    () => Object.values(processedModelSchemas.schemas || {}),
    [processedModelSchemas.schemas]
  );

  // Resolve training metadata for a schema from its linked template (by schemaType). Defaults to "no training" when the template isn't loaded or the field is missing.
  const getTrainingInfo = (model) => {
    const template = templatesById[model.schemaType];
    const requiresTraining = template?.requiresTraining ?? false;
    const trainingCost = template?.trainingCost;
    return { requiresTraining, trainingCost };
  };

  const availableCategories = useMemo(
    () => getAvailableCategories(models, templatesById),
    [models, templatesById]
  );

  // "Recently run" is dropped — this page doesn't load run history, so it would sort by nothing
  const sortOptions = useMemo(
    () => MODEL_SORT_OPTIONS.filter((option) => option.value !== "recent"),
    []
  );

  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const filteredModels = useMemo(
    () =>
      filterAndSortModels({
        models,
        templatesById,
        search: searchValue,
        categories: selectedCategories,
        sortBy,
        // This page doesn't load run history, so there is nothing for the "recent" sort to read
        latestRuns: {},
      }),
    [models, templatesById, searchValue, selectedCategories, sortBy]
  );

  if (processedModelSchemas.loading === "pending") {
    // Header, toolbar and table silhouette, so the page doesn't jump when the schemas land
    return (
      <PageContainer>
        <div role="status" aria-label="Loading models">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-2 h-7 w-56" radius="md" />
          <Skeleton className="mt-6 h-24 w-full" radius="md" />
          <div className="eai-card mt-8 overflow-hidden">
            <SkeletonGroup count={6} label="Loading model schemas" className="divide-y divide-eai-border">
              <div className="flex items-center gap-4 px-4 py-3.5">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="ml-auto h-5 w-16" radius="full" />
              </div>
            </SkeletonGroup>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Models"
        description="Manage model schemas and energy models"
      />

      {/* Shared with the Model Garden grid — search, category chips, count and sort */}
      <ModelsToolbar
        className="eai-gap-toolbar"
            searchId="models-page-search"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Search model schemas"
            availableChips={availableCategories}
            selectedChips={selectedCategories}
            onChipToggle={handleCategoryToggle}
            countLabel={`${filteredModels.length} of ${models.length} models`}
            sortBy={sortBy}
            onSortChange={setSortBy}
            sortOptions={sortOptions}
        sortAriaLabel="Sort models"
      />

      {/* DynamoDB Test Component (Temporary) */}
      <div className="mb-8">
        <TestProcessedModelSchema />
      </div>

      {/* Model Schemas Section */}
      <div className="mb-8">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="eai-h3-title">Model Schemas</h2>
              <div className="flex items-center gap-2">
                <input
                  ref={importInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleImportFileSelected}
                />
                <button
                  onClick={handleImportModel}
                  className="eai-btn-outline"
                  type="button"
                  title="Create a new model schema from an exported JSON"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15V19A2 2 0 0119 21H5A2 2 0 013 19V15M17 8L12 3M12 3L7 8M12 3V15" />
                  </svg>
                  Import
                </button>
                <button
                  onClick={handleAddModel}
                  className="eai-btn-cta"
                  type="button"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Model
                </button>
              </div>
            </div>
            
            {/* One table, so the header can't drift out of alignment with the body it labels. A wide table scrolls in its own box rather than stretching the page. */}
            <div className="eai-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-separate border-spacing-0 text-left">
                  <thead>
                    <tr>
                      <th className={HEAD_CLASS}>Name</th>
                      <th className={`${HEAD_CLASS} hidden md:table-cell`}>Type</th>
                      <th className={`${HEAD_CLASS} hidden md:table-cell`}>Schema Type</th>
                      <th className={`${HEAD_CLASS} hidden md:table-cell`}>Description</th>
                      <th className={`${HEAD_CLASS} hidden lg:table-cell`}>Version</th>
                      <th className={`${HEAD_CLASS} hidden md:table-cell`}>Status</th>
                      <th className={`${HEAD_CLASS} hidden lg:table-cell`}>Training</th>
                      <th className={`${HEAD_CLASS} hidden lg:table-cell`}>Cost</th>
                      <th className={`${HEAD_CLASS} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModels.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="eai-description-sm px-6 py-8 text-center">
                          {models.length === 0
                            ? "No model schemas created yet"
                            : "No model schemas match your search or filters"}
                        </td>
                      </tr>
                    ) : (
                      filteredModels.map((model) => {
                        const { requiresTraining, trainingCost } = getTrainingInfo(model);
                        return (
                        <tr key={model.id} className="border-b border-eai-border transition-colors hover:bg-eai-surface-hover">
                          <td className="px-4 py-3">
                            <div className="eai-card-title">{model.name}</div>
                            <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                              <code className="max-w-[130px] truncate font-mono text-eai-body-xs text-eai-ink-faint md:max-w-none" title={model.id}>
                                {model.id}
                              </code>
                              <button
                                onClick={() => handleCopyId(model.id)}
                                className="shrink-0 text-eai-ink-faint transition-colors hover:text-eai-ink"
                                title="Copy schema ID"
                                aria-label="Copy schema ID"
                                type="button"
                              >
                                {copiedId === model.id ? (
                                  <span className="font-outfit text-eai-body-xs font-medium text-eai-success-text">Copied!</span>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4A2 2 0 012 13V4A2 2 0 014 2H13A2 2 0 0115 4V5" />
                                  </svg>
                                )}
                              </button>
                            </div>
                            {/* Below md the folded-away columns collapse into one summary line */}
                            <div className="eai-description-sm md:hidden">
                              {model.type}{model.schemaType ? ` • ${model.schemaType}` : ''} • v{model.version} • {model.isActive ? 'Active' : 'Inactive'}
                              {requiresTraining && ` • Training${trainingCost != null ? ` (${trainingCost})` : ''}`}
                            </div>
                          </td>
                          <td className={CELL_CLASS}>{model.type}</td>
                          <td className={CELL_CLASS}>
                            <div className="truncate" title={model.schemaType}>
                              {model.schemaType || '—'}
                            </div>
                          </td>
                          <td className={`${CELL_CLASS} max-w-[16rem]`}>
                            <div className="truncate" title={model.description}>
                              {model.description}
                            </div>
                          </td>
                          <td className={`${CELL_CLASS} lg:table-cell md:hidden`}>v{model.version}</td>
                          <td className="hidden px-4 py-3 md:table-cell">
                            {/* Active is a real state worth colouring; inactive is just a fact */}
                            <span className={`eai-tag-pill-sm ${
                              model.isActive
                                ? 'border-eai-success-border bg-eai-success-surface-soft text-eai-success-text'
                                : ''
                            }`}>
                              {model.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="hidden px-4 py-3 lg:table-cell">
                            <span className={`eai-tag-pill-sm ${
                              requiresTraining
                                ? 'border-eai-warning-text/25 bg-eai-brand-peach text-eai-warning-text'
                                : ''
                            }`}>
                              {requiresTraining ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className={`${CELL_CLASS} lg:table-cell md:hidden`}>
                            {requiresTraining && trainingCost != null ? trainingCost : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {/* Icon row rather than five text buttons: the labels are in the tooltips, and a dropdown here would be clipped by the table's scroll box. */}
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleRunModel(model)}
                                className="eai-btn-icon"
                                title="Run model"
                                aria-label="Run model"
                                disabled={!model.isActive}
                              >
                                <FiPlay size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditModel(model)}
                                className="eai-btn-icon"
                                title="Edit model"
                                aria-label="Edit model"
                              >
                                <FiEdit2 size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePostSampleModel(model)}
                                className="eai-btn-icon"
                                title="Download sample executionUrl POST body"
                                aria-label="Download sample POST body"
                              >
                                <FiCode size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleExportModel(model)}
                                className="eai-btn-icon"
                                title="Export as JSON"
                                aria-label="Export as JSON"
                              >
                                <FiDownload size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteModel(model)}
                                className="eai-btn-icon eai-btn-icon-danger"
                                title="Delete model"
                                aria-label="Delete model"
                              >
                                <FiTrash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

      {/* Energy Models Section */}
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="eai-h3-title">Energy Models</h2>
          <button onClick={handleAddEnergyModel} className="eai-btn-cta" type="button">
            <PlusIcon className="h-4 w-4" />
            Add Energy Model
          </button>
        </div>

        {pCurve?.data || pCurve?.solarData ? (
          <div className="eai-card overflow-hidden">
            <PCurveTable
              solarEnergyRecords={Object.values(pCurve.solarData || {})}
              pCurveRecords={Object.values(pCurve.data || {})}
            />
          </div>
        ) : (
          <div className="eai-card eai-description-sm px-6 py-12 text-center">
            No energy models created yet
          </div>
        )}
      </div>
    </PageContainer>
  );
};

// Admin-nav page: gate at the component level too, so a direct URL hit by a non-admin redirects instead of rendering (issue #694, finding 4). Server-side @auth on the underlying models remains the real data protection.
export default withAdminAuth(ModelsPage);